import styles from 'common/_variables.module.scss';
import { Chart } from 'components/Chart/Chart';
import { LoadingBar } from 'components/loading-bar/loading-bar';
import { usePropertyContext } from 'context/propertyContext';
import { labels } from 'locales/en.label';
import { cloneDeep } from 'lodash';
import { useRateLevelsForDate } from 'pages/home/hooks/use-rate-levels-for-date';
import {
	type TrendData,
	useTrendDataForDate,
} from 'pages/home/hooks/use-trend-data-for-date';
import { type ReactNode, type SyntheticEvent, useState } from 'react';
import { MdLock } from 'react-icons/md';
import Carousel from 'react-material-ui-carousel';
import { buildTooltip, trendChartConfig } from '../trend-widget/config';

// The carousel only measures a slide's height when it becomes active,
// so insight slides use a fixed height to avoid clipping once data loads.
const INSIGHT_SLIDE_HEIGHT = 150;

const InsightSlide = ({
	title,
	aside,
	children,
}: {
	title: string;
	aside?: ReactNode;
	children: ReactNode;
}) => (
	// Taps inside insight slides (e.g. chart tooltips) shouldn't toggle card selection
	<div
		className='insight-slide'
		style={{ height: INSIGHT_SLIDE_HEIGHT }}
		onClick={(e) => e.stopPropagation()}
		onKeyDown={(e) => e.stopPropagation()}
	>
		<div className='insight-header'>
			<p className='label insight-title'>{title}</p>
			{aside}
		</div>
		{children}
	</div>
);

const isClosed = (status: string | undefined) =>
	status?.toUpperCase().startsWith('CLOSE') ?? false;

const OtbSlide = ({ stayDate }: { stayDate: string }) => {
	const { rateLevels, total, loading } = useRateLevelsForDate(stayDate);
	const { otb_title, rooms, closed } = labels.home.day_card_insights;

	const rows = (rateLevels ?? []).map(({ level, otb, liveOtb, status }) => ({
		level,
		value: liveOtb ?? otb ?? 0,
		closed: isClosed(status),
	}));
	// Bars share one scale so lengths are comparable across levels
	const max = Math.max(1, ...rows.map(({ value }) => value));

	return (
		<InsightSlide
			title={otb_title}
			aside={
				total !== undefined && (
					<span className='insight-aside'>
						<strong>{total}</strong> {rooms}
					</span>
				)
			}
		>
			<LoadingBar loading={loading}>
				{rateLevels && total !== undefined ? (
					<ul className='otb-bars'>
						{rows.map(({ level, value, closed: isLevelClosed }) => (
							<li
								key={level}
								className={isLevelClosed ? 'otb-bar closed' : 'otb-bar'}
								aria-label={`${level}: ${value} ${rooms}${
									isLevelClosed ? `, ${closed}` : ''
								}`}
							>
								<span className='otb-bar__level'>{level}</span>
								<span className='otb-bar__track'>
									{value > 0 && (
										<span
											className='otb-bar__fill'
											style={{ width: `${(value / max) * 100}%` }}
										/>
									)}
								</span>
								<span className='otb-bar__value'>
									{isLevelClosed && <MdLock title={closed} />}
									{value}
								</span>
							</li>
						))}
					</ul>
				) : (
					<span className='no-data'>{labels.home.no_data}</span>
				)}
			</LoadingBar>
		</InsightSlide>
	);
};

// Height left for the chart after the slide header
const TREND_CHART_HEIGHT = INSIGHT_SLIDE_HEIGHT - 26;

const buildCompactTrendOptions = (
	{ bwRates, marketRates, recRates }: TrendData,
	currencySymbol: string,
) => {
	const config = cloneDeep(trendChartConfig);
	const axisLabelStyle = { color: styles.mainGrey, fontSize: '0.625rem' };

	config.chart = {
		...config.chart,
		type: 'line',
		height: TREND_CHART_HEIGHT,
		spacing: [4, 4, 0, 0],
		backgroundColor: 'transparent',
	};
	config.title = { text: undefined };
	config.subtitle = { text: undefined };
	config.legend = {
		...config.legend,
		width: undefined,
		itemWidth: undefined,
		maxHeight: undefined,
		margin: 4,
		padding: 0,
		itemDistance: 12,
		symbolWidth: 10,
		itemStyle: { ...config.legend.itemStyle, fontSize: '0.625rem' },
	};
	config.plotOptions = {
		line: { marker: { enabled: true, radius: 2 }, lineWidth: 2 },
	};
	config.xAxis.labels.style = axisLabelStyle;
	// Rates only: pickup is shown in the slide header to avoid a second y-axis.
	// Colors match the desktop trend widget so each series keeps its identity.
	config.series = [
		{ name: 'BWH Rate', color: styles.graphBrown, data: bwRates },
		{ name: 'Market Rate', color: styles.graphLightBlue, data: marketRates },
		{ name: 'Rec Rate', color: styles.graphOrange, data: recRates },
	];
	config.yAxis = {
		title: { text: undefined },
		labels: { style: axisLabelStyle, format: `${currencySymbol}{value}` },
		tickAmount: 3,
		gridLineColor: 'rgba(196, 196, 196, 0.2)',
	};
	config.tooltip = buildTooltip(currencySymbol);

	return config;
};

const formatPickup = (pickupData: TrendData['pickupData']) => {
	const total = pickupData.reduce<number>(
		(sum, value) => sum + (value ?? 0),
		0,
	);
	return total > 0 ? `+${total}` : `${total}`;
};

const TrendSlide = ({ stayDate }: { stayDate: string }) => {
	const { currencySymbol } = usePropertyContext();
	const { data, loading } = useTrendDataForDate(stayDate);
	const { trend_title, pickup } = labels.home.day_card_insights;

	return (
		<InsightSlide
			title={trend_title}
			aside={
				data && (
					<span className='insight-aside'>
						{pickup} <strong>{formatPickup(data.pickupData)}</strong>
					</span>
				)
			}
		>
			<LoadingBar loading={loading}>
				{data ? (
					<Chart options={buildCompactTrendOptions(data, currencySymbol)} />
				) : (
					<span className='no-data'>{labels.home.no_data}</span>
				)}
			</LoadingBar>
		</InsightSlide>
	);
};

type DayCardCarouselProps = {
	stayDate: string;
	children: ReactNode;
};

/**
 * Mobile-only carousel for a day card. The first slide is the regular card
 * summary; swiping reveals metrics that only fit on the desktop dashboard.
 * Insight slides mount on first view so each card doesn't fire extra queries.
 */
export const DayCardCarousel = ({
	stayDate,
	children,
}: DayCardCarouselProps) => {
	const [visited, setVisited] = useState<Set<number>>(() => new Set([0]));

	const slides = [
		<div key='summary'>{children}</div>,
		<div key='otb'>
			{visited.has(1) ? (
				<OtbSlide stayDate={stayDate} />
			) : (
				<InsightSlide title={labels.home.day_card_insights.otb_title}>
					{null}
				</InsightSlide>
			)}
		</div>,
		<div key='trend'>
			{visited.has(2) ? (
				<TrendSlide stayDate={stayDate} />
			) : (
				<InsightSlide title={labels.home.day_card_insights.trend_title}>
					{null}
				</InsightSlide>
			)}
		</div>,
	];

	// Indicator taps shouldn't toggle card selection
	const stopIndicatorPropagation = (e: SyntheticEvent) => {
		if ((e.target as HTMLElement).closest('.day-card-carousel__indicators')) {
			e.stopPropagation();
		}
	};

	return (
		<div
			onClick={stopIndicatorPropagation}
			onKeyDown={stopIndicatorPropagation}
		>
			<Carousel
				className='day-card-carousel'
				autoPlay={false}
				animation='slide'
				swipe
				cycleNavigation={false}
				navButtonsAlwaysInvisible
				indicatorContainerProps={{
					className: 'day-card-carousel__indicators',
				}}
				onChange={(now) => {
					if (now === undefined || visited.has(now)) return;
					setVisited((prev) => new Set(prev).add(now));
				}}
			>
				{slides}
			</Carousel>
		</div>
	);
};
